import { GMXPanel } from '../panel/panel'
import { GMXVehicleSelectorFormElement } from '../../form-fields/vehicle-selector/vehicle-selector'
import { GMXDealerSelector } from '../../dealer-selector/dealer-selector'

const isBlankValue = (value: unknown): boolean =>
	value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

/** An object is blank when every value it holds, at any depth, is blank. */
const isBlankObject = (value: object): boolean =>
	Object.values(value).every(nested =>
		nested !== null && typeof nested === 'object' ? isBlankObject(nested) : isBlankValue(nested)
	)

/**
 * Copy the submit body without nested objects whose values are all blank, so an optional
 * group the user never filled in — phoneNumber1 being the common one — is omitted rather
 * than submitted as an empty shell. Blank scalars are kept; only whole objects are dropped.
 */
const pruneBlankObjects = (source: Record<string, unknown>): Record<string, unknown> => {
	const pruned: Record<string, unknown> = {}

	Object.entries(source).forEach(([key, value]) => {
		if (value === null || typeof value !== 'object') {
			pruned[key] = value
			return
		}

		if (isBlankObject(value)) {
			return
		}

		pruned[key] = pruneBlankObjects(value as Record<string, unknown>)
	})

	return pruned
}

/**
 * Form
 */
export class GMXForm extends HTMLElement {
	formEl: HTMLFormElement
	formStepEls: NodeListOf<HTMLElement>
	thankYouStepEl: HTMLElement
	submitErrorMessageEl: HTMLElement
	parentPanelEl: GMXPanel
	submitBtn: HTMLButtonElement

	private currActiveStep = 1
	private isSubmitting = false

	private boundComponentClickHandler = this._handleComponentClick.bind(this)
	private boundFormSubmitHandler = this._handleFormSubmit.bind(this)
	private boundResetHandler = this.reset.bind(this)

	get formSubmitEndpoint(): string {
		return this.getAttribute('data-submit-endpoint')
	}
	set formSubmitEndpoint(val: string) {
		this.setAttribute('data-submit-endpoint', val)
	}

	get formTranslations(): Record<string, string> {
		return this.hasAttribute('data-form-translations')
			? JSON.parse(this.getAttribute('data-form-translations'))
			: {}
	}
	set formTranslations(val: Record<string, string>) {
		this.setAttribute('data-form-translations', JSON.stringify(val))
	}

	connectedCallback(): void {
		this.formEl = this.querySelector('form')
		this.formStepEls = this.formEl.querySelectorAll('.step')
		this.thankYouStepEl = this.querySelector('.final-thank-you-step .step')
		this.submitErrorMessageEl = this.querySelector('.submit-error-msg')
		this.parentPanelEl = this.closest('gmx-panel') as GMXPanel
		this.submitBtn = this.querySelector('button[type="submit"]')

		// If the form is NOT inside a panel, want to look for any data to prepopulate from query string parameters
		if (!this.parentPanelEl) {
			this._prepopulateFormDataFromQSPs()
		}

		this.addEventListener('click', this.boundComponentClickHandler)
		this.formEl.addEventListener('submit', this.boundFormSubmitHandler)

		if (this.parentPanelEl?.panelType === 'form-panel') {
			this.parentPanelEl.addEventListener('close', this.boundResetHandler)
		}

		this._updateState()
	}

	disconnectedCallback(): void {
		this.removeEventListener('click', this.boundComponentClickHandler)
		this.formEl.removeEventListener('submit', this.boundFormSubmitHandler)
		this.parentPanelEl?.removeEventListener('close', this.boundResetHandler)
	}

	/**
	 * Reset the form to its initial state: first step active, inputs and error states cleared,
	 * and vehicle/dealer selectors reset.
	 */
	reset(): void {
		this._setSubmitting(false)

		this.currActiveStep = 1
		this._updateState()

		this.thankYouStepEl?.classList.remove('active')
		this.thankYouStepEl
			?.querySelectorAll('.thank-you-selected-dealers, .thank-you-selected-vehicle')
			.forEach(el => el.remove())

		this.submitErrorMessageEl?.setAttribute('hidden', '')

		this.formEl?.reset()

		this.querySelectorAll('.invalid').forEach(el => el.classList.remove('invalid'))
		this.formEl?.querySelectorAll('.filled').forEach(el => el.classList.remove('filled'))
		this.formEl
			?.querySelectorAll('gmx-checkbox-group input[type="checkbox"][disabled]')
			.forEach(el => el.removeAttribute('disabled'))

		const vehicleSelectorEl = this.formEl?.querySelector(
			'gmx-vehicle-selector-form-element'
		) as GMXVehicleSelectorFormElement | null
		const dealerSelectorEl = this.formEl?.querySelector('gmx-dealer-selector') as GMXDealerSelector | null

		vehicleSelectorEl?.reset()
		dealerSelectorEl?.reset()
	}

	/**
	 * Callback to handle click event of component.
	 * @param {Event} e
	 * @private
	 */
	private _handleComponentClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('next-step-btn')) {
			this._goToNextStep()
		} else if (target.classList.contains('prev-step-btn')) {
			const currentStep = this.formStepEls[this.currActiveStep - 1]
			const prevStep = this.formStepEls[this.currActiveStep - 2]
			currentStep.classList.remove('active')
			prevStep.classList.add('active')
			this.currActiveStep--
		}
	}

	/**
	 * Validate the active step and, when it passes, reveal the one after it.
	 * @private
	 */
	private _goToNextStep(): void {
		const currentStep = this.formStepEls[this.currActiveStep - 1]

		if (!this._validateStep(currentStep)) {
			return
		}

		currentStep.classList.remove('active')
		this.formStepEls[this.currActiveStep].classList.add('active')
		this.currActiveStep++
	}

	/**
	 * Handle managing state of the form (ie, what is the current active step, etc.
	 * @private
	 */
	private _updateState(): void {
		this.formStepEls.forEach((step, index) => {
			if (index + 1 === this.currActiveStep) {
				step.classList.add('active')
			} else {
				step.classList.remove('active')
			}
		})
	}

	private _validateStep(stepEl: HTMLElement): boolean {
		const inputFields = stepEl.querySelectorAll('input')
		let isValid = true
		inputFields.forEach(inputEl => {
			if (
				(inputEl.getAttribute('aria-required') === 'true' || inputEl.required) &&
				(inputEl.value === '' || (inputEl.type === 'checkbox' && !inputEl.checked))
			) {
				const errorEl = inputEl.parentElement.querySelector('.error-text') as HTMLElement

				if (errorEl) {
					errorEl.innerText = this.formTranslations.generalRequiredErrorMsg || 'Error'
				}

				isValid = false
				inputEl.classList.add('invalid')
			} else if (!inputEl.checkValidity()) {
				const errorEl = inputEl.parentElement.querySelector('.error-text') as HTMLElement

				if (errorEl) {
					errorEl.innerText =
						inputEl.getAttribute('data-custom-invalid-error-msg') ||
						this.formTranslations.generalInvalidErrorMsg ||
						'Error'
				}

				isValid = false
				inputEl.classList.add('invalid')
			} else {
				inputEl.classList.remove('invalid')
			}
		})

		const dropdownFields = stepEl.querySelectorAll('select')
		dropdownFields.forEach(dropdownEl => {
			if ((dropdownEl.hasAttribute('aria-required') || dropdownEl.required) && dropdownEl.value === '') {
				const errorEl = dropdownEl.parentElement.querySelector('.error-text') as HTMLElement

				if (errorEl) {
					errorEl.innerText =
						dropdownEl.getAttribute('data-custom-invalid-error-msg') ||
						this.formTranslations.generalRequiredErrorMsg ||
						'Error'
				}

				isValid = false
				dropdownEl.classList.add('invalid')
			} else {
				dropdownEl.classList.remove('invalid')
			}
		})

		const checkboxGroups = stepEl.querySelectorAll('gmx-checkbox-group')
		checkboxGroups.forEach(cg => {
			if (cg.hasAttribute('aria-required')) {
				if (!cg.querySelectorAll('input:checked').length) {
					const errorEl = cg.querySelector('.error-text') as HTMLElement

					if (errorEl) {
						errorEl.innerText =
							cg.getAttribute('data-custom-invalid-error-msg') ||
							this.formTranslations.generalRequiredErrorMsg ||
							'Error'
					}

					isValid = false
					cg.classList.add('invalid')
				} else {
					cg.classList.remove('invalid')
				}
			}
		})

		const dealerSelectors = stepEl.querySelectorAll('gmx-dealer-selector')
		dealerSelectors.forEach(ds => {
			if (ds.hasAttribute('aria-required')) {
				if (!ds.querySelectorAll('.dealer-list input[type="checkbox"]:checked').length) {
					isValid = false
					ds.classList.add('invalid')
				} else {
					ds.classList.remove('invalid')
				}
			}
		})

		return isValid
	}

	private async _handleFormSubmit(e: Event): Promise<void> {
		e.preventDefault()
		e.stopPropagation()

		if (this.isSubmitting) {
			return
		}

		const activeStepEl = this.formStepEls.length ? this.formStepEls[this.currActiveStep - 1] : null
		const nextStepEl = this.formStepEls[this.currActiveStep]

		// Pressing Enter in a field implicitly clicks the submit button even while an earlier step is showing
		if (activeStepEl && nextStepEl && this.submitBtn && !activeStepEl.contains(this.submitBtn)) {
			this._goToNextStep()
			return
		}

		if (!this._validateStep(activeStepEl || this.formEl)) {
			return
		}

		const formData = new FormData(this.formEl)

		const submitBodyObj = {}
		for (const fromEntriesKey in Object.fromEntries(formData)) {
			if (fromEntriesKey.match(/\[\]$/)) {
				// Is an array
				const submitBodyKey = fromEntriesKey.replace(/\[\]$/, '')

				this._addToFormSubmitBody(submitBodyObj, submitBodyKey, formData.getAll(fromEntriesKey).join(','))
			} else {
				this._addToFormSubmitBody(submitBodyObj, fromEntriesKey, formData.get(fromEntriesKey) as string)
			}
		}

		const eventId = this._getAnalyticsEventId()
		if (eventId) {
			this._addToFormSubmitBody(submitBodyObj, 'salesLead.eventId', eventId)
		}

		const submitBody = pruneBlankObjects(submitBodyObj)

		this._setSubmitting(true)

		try {
			const response = await fetch(this.formSubmitEndpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(submitBody),
			})

			if (response.ok) {
				this.formStepEls[this.currActiveStep - 1]?.classList.remove('active')

				if (this.thankYouStepEl) {
					this._populateThankYouStep()
					this.thankYouStepEl.classList.add('active')
					this.thankYouStepEl.scrollIntoView({ block: 'start' })
				}

				this.submitErrorMessageEl.setAttribute('hidden', '')
			} else {
				throw new Error(`Failed to submit form.  Response status: ${response.status}`)
			}
		} catch (error) {
			window.console.error(`ERROR:  Form Submission Failure: ${error}`)
			this.submitErrorMessageEl.removeAttribute('hidden')
		} finally {
			this._setSubmitting(false)
		}
	}

	/**
	 * Read the analytics transaction id the tag management system stores as `eventid`.
	 * Returns an empty string when it has not been set, or when storage access throws
	 * (private browsing, blocked third-party storage).
	 * @private
	 */
	private _getAnalyticsEventId(): string {
		try {
			return window.localStorage.getItem('eventid')?.trim() || ''
		} catch {
			return ''
		}
	}

	/**
	 * Disable submit and panel close buttons while a submission request is in flight, and prevent
	 * the parent panel from closing until the request succeeds or fails.
	 */
	private _setSubmitting(isSubmitting: boolean): void {
		this.isSubmitting = isSubmitting

		if (this.parentPanelEl) {
			this.parentPanelEl.closeLocked = isSubmitting
		}

		const root: ParentNode = this.parentPanelEl || this
		root.querySelectorAll('button[type="submit"], button.close-btn').forEach(btn => {
			if (isSubmitting) {
				btn.setAttribute('disabled', '')
			} else {
				btn.removeAttribute('disabled')
			}
		})

		if (isSubmitting) {
			this.submitBtn.classList.add('processing')
			this.submitBtn.setAttribute('aria-busy', '')
		} else {
			this.submitBtn.classList.remove('processing')
			this.submitBtn.removeAttribute('aria-busy')
		}
	}

	private _populateThankYouStep(): void {
		if (!this.thankYouStepEl) {
			return
		}

		const thankYouDynamicContentContainer = this.thankYouStepEl.querySelector(
			'.thank-you-dynamic-content-container'
		)

		if (thankYouDynamicContentContainer) {
			// First, Get Dealer Selector Info
			const dealerSelectorEl = this.formEl.querySelector('gmx-dealer-selector')
			if (dealerSelectorEl) {
				const selectedDealers = dealerSelectorEl.querySelectorAll('.dealer-list input[type="checkbox"]:checked')

				if (selectedDealers.length) {
					const thankYouSelectedDealerList = document.createElement('ul')
					thankYouSelectedDealerList.classList.add(
						'gmx-text-list',
						'layout-stacked',
						'style-title-plus-address-checkbox',
						'thank-you-selected-dealers'
					)
					selectedDealers.forEach(dlcb => {
						const dealerLabel = dlcb.closest('li')

						if (dealerLabel) {
							const newDealerListItem = dealerLabel.cloneNode(true) as HTMLElement
							newDealerListItem.querySelector('input')?.remove()

							thankYouSelectedDealerList.appendChild(newDealerListItem)
						}
					})

					thankYouDynamicContentContainer.after(thankYouSelectedDealerList)
				}
			}

			// Next Get vehicle selector info
			const vehicleSelectorEl = this.formEl.querySelector('gmx-vehicle-selector-form-element')
			if (vehicleSelectorEl) {
				const thankYouSelectedVehicleEl = document.createElement('div')
				thankYouSelectedVehicleEl.classList.add('thank-you-selected-vehicle')

				const selectedVehicleModelYearName = (
					vehicleSelectorEl.querySelector(
						'gmx-dropdown[data-dropdown-name="vehicleYear"] select'
					) as HTMLSelectElement
				)?.selectedOptions[0]?.textContent
				const selectedVehicleModelName = (
					vehicleSelectorEl.querySelector(
						'gmx-dropdown[data-dropdown-name="vehicleModel"] select'
					) as HTMLSelectElement
				)?.selectedOptions[0]?.textContent
				if (selectedVehicleModelName) {
					const vehicleModelEl = document.createElement('div')
					vehicleModelEl.classList.add('thank-you-vehicle-model-description')
					vehicleModelEl.textContent = `${selectedVehicleModelYearName} ${selectedVehicleModelName}`
					thankYouSelectedVehicleEl.appendChild(vehicleModelEl)
				}

				const selectedVehicleImage = vehicleSelectorEl.querySelector('img') as HTMLImageElement
				if (selectedVehicleImage) {
					const thankYouImg = selectedVehicleImage.cloneNode(true) as HTMLImageElement
					thankYouImg.classList.add('thank-you-vehicle-img')
					thankYouSelectedVehicleEl.appendChild(thankYouImg)
				}

				thankYouDynamicContentContainer.after(thankYouSelectedVehicleEl)
			}
		}
	}

	/**
	 * Method to add a key/value pair to the form body object
	 *
	 * @param formBodyObj - Form Body Object
	 * @param key {string} - Key to add to the object (ex: salesLead.firstName, brand, etc.)
	 * @param value {string} - Value to associate to the key in the object
	 * @private
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private _addToFormSubmitBody(formBodyObj: any, key: string, value: string): void {
		const keys = key.split('.')

		let current = formBodyObj

		for (let i = 0; i < keys.length; i++) {
			const k = keys[i]
			if (k === '__proto__' || k === 'constructor' || k === 'prototype') {
				// Prevent prototype pollution
				return
			}

			if (i === keys.length - 1) {
				current[k] = value
			} else {
				current[k] = current[k] || {}
				current = current[k]
			}
		}
	}

	private _prepopulateFormDataFromQSPs(): void {
		// First, check for any query string parameters for the vehicle selector and prepopulate those values if they exist
		const modelYear = new URLSearchParams(window.location.search).get('x-modelyear')
		const model = new URLSearchParams(window.location.search).get('x-bodystyle')
		const vehicleSelectorEl = this.querySelector('gmx-vehicle-selector-form-element')

		if (vehicleSelectorEl) {
			if (modelYear) {
				vehicleSelectorEl.setAttribute('data-default-year', modelYear)

				if (model) {
					vehicleSelectorEl.setAttribute('data-default-model', model)
				}
			}
		}
	}
}

if (!customElements.get('gmx-form')) {
	customElements.define('gmx-form', GMXForm)
}
